import { Alert, Chip, FormControlLabel, Grid, Stack, Switch, Typography, styled, useTheme } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { BasketItemStatus } from "../../../Core/Api/Types/basket-item-status";
import { DcpKdmGenerateProfile } from "../../../Core/Api/Types/dcp-kdm-generate-profile";
import { OrderProductType } from "../../../Core/Api/Types/order-product-type";
import { ConfigBaseProps } from "../../../Pages/Basket/basket";
import {
  ProductField,
  UpdateItemProductRootOnBasket,
  UpdateItemSettingsOnBasket,
  UpdateItemStatusOnBasket,
} from "../../../Redux/Reducers/redux-slice-basket";
import { FormExists, GetFormIsValid, GetSectionIsValid } from "../../../Redux/Reducers/redux-slice-form-validation";
import { useAppDispatch, useAppSelector } from "../../../Redux/redux-store";
import { GetOrderProductReadableType, Distinct, True } from "../../../Utils/helpers";
import ConfigHeader from "../ConfigHeader/config-header";
import ConfigSaveButton from "../ConfigSaveButton/config-save-button";
import ConfigFormIcon from "../ConfigFormIcon";
import FormSection from "../../Forms/FormSection/form-section";
import FormAutocomplete from "../../Forms/FormAutocomplete/form-autocomplete";
import { DbAssetObjectInfos } from "../../../Core/Api/Types/db-asset-object-infos";
import dayjs from "dayjs";
import { DbKdmRecipientInfos } from "../../../Core/Api/Types/db-kdm-recipient-infos";
import GlobalConst from "../../../Core/Utils/Constants/globals";
import ConfigCommonSectionFormDelivery from "../ConfigCommon/config-common-section-form-delivery";
import { DbStorageInfos } from "../../../Core/Api/Types/db-storage-infos";
import { MailingListDto } from "../../../Core/Api/Types/get-mailing-list-response";
import { GetKdmRecipientLabel } from "../../../Utils/Helpers/IntelliTranskode/intellitranskode-helpers";
import { immerable } from "immer";
import ConfigCommonSectionFormScheduling from "../ConfigCommon/config-common-section-form-scheduling";
import FormInputDateTime from "../../Forms/FormInputDate/form-input-date-time";
import { GetMetadataByTagName } from "../../../Core/Utils/asset-object-utils";
import { useTranslation } from "react-i18next";

interface ConfigDcpKdmGeneration extends ConfigBaseProps {
  storages: DbStorageInfos[];
  maillists: MailingListDto[];
}

const ConfigDcpKdmGeneration = (props: ConfigDcpKdmGeneration) => {
  const { t } = useTranslation();
  const { handleSubmit, handleChangeProduct, handleCancel, item, storages, maillists, ...stackProps } = props;

  const [searchPattern, setSearchPattern] = useState<string>("");
  const [toogleShowRoom, setToogleShowRoom] = useState<boolean>(false);
  const theme = useTheme();

  const formId = useMemo(() => `form-dcp-kdm-generation-${item.id}`, []);
  const formValid = useAppSelector(GetFormIsValid(formId));
  const formExists = useAppSelector(FormExists(formId));
  const kdmGenerationSectionId = useMemo(() => `${formId}-configure-section`, []);

  const dispatch = useAppDispatch();

  const settings: DcpKdmGenerateProfile = useMemo(
    () => item.selectedProduct?.settings,
    [item.selectedProduct?.settings]
  );

  const onChangeRecipientSite = (value) => {
    const newSettings = { ...settings };
    let recipientIds = Object.assign([], newSettings.selectedRecipientIds);
    let emails: string[] | null = null;

    if (value.filter((x) => x.id === -1).length > 0 && searchPattern !== "") {
      if (recipientIds === null) recipientIds = [];
      if (emails === null) emails = [];

      settings.recipients
        .filter((x) => GetKdmRecipientLabel(x)?.includesAllWords(searchPattern))
        .forEach((x) => {
          recipientIds.push(x.id);
          if (x.contact?.email) emails!.push(x.contact.email);
        });

      setSearchPattern("");
    } else {
      recipientIds = value.map((val) => val.id);
      if (recipientIds.some((x) => x == -1)) return;
      emails = value.filter((x) => x.contact?.email).map((x) => x.contact.email);
    }

    newSettings.selectedRecipientIds = recipientIds;
    handleUpdateProductRoot(ProductField.Emails, emails?.filter(Distinct));
    updateSettings(newSettings);
  };

  const isConfigureSectionValid = useAppSelector(GetSectionIsValid(formId, kdmGenerationSectionId));

  // Update settings
  const updateSettings = (settings: DcpKdmGenerateProfile) => {
    dispatch(UpdateItemSettingsOnBasket({ itemId: item.id, settings: settings }));
  };

  // Update product root base object into redux
  const handleUpdateProductRoot = (field: ProductField, value: any) => {
    dispatch(UpdateItemProductRootOnBasket({ itemId: item.id, field: field, value: value }));
  };

  useEffect(() => {
    if (!formValid && formExists) dispatch(UpdateItemStatusOnBasket({ itemId: item.id, status: BasketItemStatus.New }));
  }, [formValid]);

  useEffect(() => {
    const newSettings = { ...settings };
    newSettings.dateIn = dayjs(settings.dateIn).startOf("day").toJSON();
    newSettings.dateOut = dayjs(settings.dateOut).endOf("day").toJSON();
    updateSettings(newSettings);
  }, []);

  const selectAll: DbKdmRecipientInfos = {
    platformId: null,
    creationInfos: null,
    updateInfos: null,
    serialNumber: null,
    address: null,
    zipCode: null,
    city: null,
    region: null,
    country: null,
    name: "Select all",
    roomName: null,
    type: null,
    theater: null,
    projector: null,
    globecast: null,
    cert: null,
    contact: null,
    id: -1,
    isNew: false,
    [immerable]: true,
  };

  const limitTags = 10;

  return (
    <Stack width={"100%"} {...stackProps} /*display={formExists ? "" : "none"}*/>
      <ConfigHeader
        configType={item.selectedProduct?.type ? item.selectedProduct?.type : OrderProductType.DcpKdmGeneration}
        title={GetOrderProductReadableType(
          item.selectedProduct?.type ? item.selectedProduct?.type : OrderProductType.DcpKdmGeneration
        )}
        handleCancel={handleCancel}
        handleChangeProduct={handleChangeProduct}
        itemId={item.id}
      />
      {settings.dkdmExists ? (
        <Stack spacing={1.5} width={"100%"} {...stackProps}>
          <FormSection
            icon={<ConfigFormIcon icon="meta" />}
            title={t("form:section.title.configKdmGeneration")}
            status={isConfigureSectionValid ? "complete" : "warning"}
            statusText={isConfigureSectionValid ? t("common:configured") : t("common:configurationRequired")}
          >
            <Stack spacing={1} width={"100%"} {...stackProps}>
              <FormAutocomplete
                label={t("field:label.selectCplAsset")}
                validationProps={{
                  formId: formId,
                  formSectionId: kdmGenerationSectionId,
                  fieldId: "select-cpl",
                  required: true,
                }}
                onChange={(value) => {
                  const newSettings = { ...settings };
                  newSettings.selectedCplAssetId = value?.id;
                  updateSettings(newSettings);
                }}
                options={settings.cpls}
                value={settings.cpls.find((c) => c.id === settings.selectedCplAssetId) || null}
                getOptionLabel={(option) => {
                  if (GetMetadataByTagName(option as DbAssetObjectInfos, "dcpCplName"))
                    return GetMetadataByTagName(option as DbAssetObjectInfos, "dcpCplName")?.value;
                  else if (settings.cpls.length === 1)
                    return GetMetadataByTagName(item.assetInfos, "dcpCplNames")?.value.find(True);
                  else return (option as DbAssetObjectInfos).name;
                }}
              />
              <Grid item md={4}>
                <Typography color="text.tier">
                  <FormControlLabel
                    sx={{ mt: 1.5, ml: 0 }}
                    control={
                      <Switch
                        size="small"
                        checked={toogleShowRoom}
                        onChange={() => setToogleShowRoom(!toogleShowRoom)}
                      />
                    }
                    label={t("field:label.showRooms")}
                  />
                </Typography>
              </Grid>
              <FormAutocomplete
                label={t("field:label.selectSites")}
                multiple
                validationProps={{
                  formId: formId,
                  formSectionId: kdmGenerationSectionId,
                  fieldId: "select-recipient",
                  required: true,
                }}
                onInputChange={(e, value, reason) => {
                  if (reason === "input") setSearchPattern(value);
                }}
                inputValue={searchPattern}
                onChange={onChangeRecipientSite}
                options={[selectAll, ...settings.recipients]}
                value={settings.recipients.filter((c) => settings.selectedRecipientIds?.includes(c.id)) || []}
                getOptionLabel={(option) => GetKdmRecipientLabel(option as DbKdmRecipientInfos)}
                filterOptions={(x, s) =>
                  x.filter((a) => a.id === -1 || GetKdmRecipientLabel(a)?.includesAllWords(searchPattern))
                }
                renderTags={(value, getTagProps) => {
                  const numTags = value.length;

                  return (
                    <>
                      {value.slice(0, limitTags).map((option, index) => (
                        <Chip {...getTagProps({ index })} key={index} label={GetKdmRecipientLabel(option)} />
                      ))}

                      {numTags > limitTags && ` +${numTags - limitTags}`}
                    </>
                  );
                }}
                groupBy={(option) => `${option.name} ${option.address ?? option.city ?? ""}`}
                renderGroup={(params) => {
                  return (
                    <li key={params.key}>
                      <GroupHeader
                        sx={{
                          backgroundColor:
                            !toogleShowRoom && (params.children as any[])?.some((x) => x.props["aria-selected"])
                              ? theme.palette.primary.alpha
                              : "none",
                          color: toogleShowRoom ? theme.palette.primary.main : "none",
                        }}
                        onClick={() => {
                          let elementsToAdd: any[] = [];
                          let concernedRecip = searchPattern
                            ? settings.recipients.filter(
                                (a) => a.id === -1 || GetKdmRecipientLabel(a)?.includesAllWords(searchPattern)
                              )
                            : settings.recipients;
                          (params.children as any[])?.forEach((element) => {
                            elementsToAdd.push(
                              concernedRecip.filter((x) => element.key === GetKdmRecipientLabel(x)).find(True)
                            );
                          });
                          onChangeRecipientSite([
                            ...settings.recipients.filter((c) => settings.selectedRecipientIds?.includes(c.id)),
                            ...elementsToAdd,
                          ]);
                        }}
                      >
                        {`${params.group} (${(params.children as any[]).length} Room)`}
                      </GroupHeader>
                      {toogleShowRoom && <GroupItems>{params.children}</GroupItems>}
                    </li>
                  );
                }}
              />
              <Stack direction="row" spacing={1} justifyContent="space-between">
                <FormInputDateTime
                  label={t("field:label.validFrom")}
                  format={GlobalConst.MOMENT_DATE_FORMAT_FULL}
                  hideReset
                  validationProps={{
                    formId: formId,
                    formSectionId: kdmGenerationSectionId,
                    fieldId: "date-in",
                    required: true,
                    before: new Date(settings.dateOut),
                    initialValue: new Date(settings.dateIn),
                  }}
                  value={dayjs(settings.dateIn)}
                  onChange={(value) => {
                    if (dayjs(value).isValid())
                      updateSettings({
                        ...settings,
                        dateIn: dayjs(value).format(GlobalConst.MOMENT_DATE_FORMAT_FULL),
                      });
                  }}
                />
                <FormInputDateTime
                  label={t("field:label.validUntil")}
                  format={GlobalConst.MOMENT_DATE_FORMAT_FULL}
                  hideReset
                  validationProps={{
                    formId: formId,
                    formSectionId: kdmGenerationSectionId,
                    fieldId: "date-out",
                    required: true,
                    after: new Date(settings.dateIn),
                    initialValue: new Date(settings.dateOut),
                  }}
                  value={dayjs(settings.dateOut)}
                  onChange={(value) => {
                    if (dayjs(value).isValid())
                      updateSettings({
                        ...settings,
                        dateOut: dayjs(value).format(GlobalConst.MOMENT_DATE_FORMAT_FULL),
                      });
                  }}
                />
              </Stack>
            </Stack>
          </FormSection>
          <ConfigCommonSectionFormDelivery formid={formId} item={item} maillists={maillists} storages={storages} />
          <ConfigCommonSectionFormScheduling item={item} formid={formId} />
        </Stack>
      ) : (
        <Alert severity="warning">{t("alert:warning.noDkdmWasFound")}</Alert>
      )}

      <ConfigSaveButton item={item} onSubmit={handleSubmit} disabled={!formValid} />
    </Stack>
  );
};

const GroupHeader = styled("div")(({ theme }) => ({
  position: "sticky",
  top: "-8px",
  padding: "4px 10px",
  cursor: "pointer",
}));

const GroupItems = styled("ul")({
  padding: 0,
});

export default ConfigDcpKdmGeneration;
